import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Headline,
  Loading,
  Refusal,
  Tooltip,
} from "@lopie/kernel-ui";

/**
 * Where each app's data comes from (LO-94).
 *
 * ## The question this screen exists to make askable
 *
 * Installing a micro-app used to be: provision it, and then separately
 * remember that it needs people and that Google is what produces those. The
 * connection lived in whoever set both up, so the second deployment of an app
 * was an act of memory.
 *
 * A manifest now says what an app **needs** — a different list from what it
 * may read, pointing the other way — and this screen turns each of those into
 * a question with a small number of right answers.
 *
 * ## The options are the kernel's, never this screen's
 *
 * `satisfiable_by` arrives per requirement and is derived from the concept
 * registry at request time. There is no list of connectors in this file, and
 * that is the same rule that keeps columns coming from `/schema`: a connector
 * added to the kernel appears here with no UI change. The test is real — the
 * day a second source produces employees, this dropdown grows on its own.
 *
 * ## Three sentences that must not collapse into one
 *
 * **Not configured** — nobody has answered. **Blank** — somebody answered, and
 * the answer is that nothing seeds it. **Configured and empty** — somebody
 * pointed it at a connector and that connector has not run yet. An install
 * screen that rendered all three as "no data" would be the exact failure this
 * platform argues against, so each gets its own words.
 *
 * And none of them claims a record exists. `configured` means every question
 * was answered, which is why the footer says so out loud rather than showing
 * a green tick that overstates.
 */

const STATES = {
  not_configured: {
    label: "Not set up",
    colour: "kernel.attention",
    line: "Nobody has said where this app's data should come from.",
  },
  incomplete: {
    label: "Partly set up",
    colour: "kernel.attention",
    line: "Some of it is answered and some is not.",
  },
  configured: {
    label: "Set up",
    colour: "kernel.permitted",
    line: "Every question has an answer. That is not the same as having data.",
  },
};

/**
 * What one answer means, in words rather than as a value.
 *
 * `connector:google` is precise and tells a reader nothing about what will
 * happen. This is the sentence beside it.
 */
function describe(source) {
  if (!source || source === "unset") {
    return "No answer yet. Until there is one, an empty app and a working app look the same.";
  }
  if (source === "blank") {
    return "Nothing seeds this. It gets filled in inside the app, or by hand, or not at all — and that is a decision rather than an omission.";
  }
  if (source.startsWith("connector:")) {
    const name = source.slice("connector:".length);
    return `The ${name} connector produces these records. Whether it is configured and running is the Connectors screen's answer, not this one's.`;
  }
  // A kernel rolled back a version renders the odd row rather than failing to
  // load. Saying what it does not recognise beats rendering a raw value as if
  // it were understood.
  return `Stored as ${source}, which this build does not recognise. It was written by a version of the kernel that knew about it.`;
}

function Requirement({ appType, requirement, onChoose }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const chosen = requirement.choice?.source || "unset";
  const options = requirement.satisfiable_by || [];

  const choose = async (source) => {
    setBusy(true);
    setFailure(null);
    try {
      await onChoose(appType, requirement.key, source);
    } catch (e) {
      setFailure(e?.body?.detail || e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "baseline", flexWrap: "wrap" }}
      >
        <Typography sx={{ fontFamily: "monospace" }}>{requirement.type}</Typography>
        {requirement.tags.length ? (
          requirement.tags.map((t) => (
            <Chip key={t} size="small" variant="outlined" label={t} />
          ))
        ) : (
          <Tooltip title="Untagged, which is the widest case — a requirement with no tag is about records that carry none.">
            <Chip size="small" variant="outlined" label="no tag" />
          </Tooltip>
        )}
        {requirement.optional ? (
          <Chip size="small" variant="outlined" label="optional" />
        ) : null}
        {requirement.in_ceiling ? null : (
          <Tooltip title="This app says it needs these and its manifest does not permit reading them. One of the two is wrong and only a person knows which.">
            <Chip
              size="small"
              variant="outlined"
              label="outside its ceiling"
              sx={{ color: "kernel.refused", borderColor: "kernel.refused" }}
            />
          </Tooltip>
        )}
      </Stack>

      {requirement.purpose ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {requirement.purpose}
        </Typography>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1.5, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          select
          size="small"
          label="Data source"
          value={chosen === "unset" ? "" : chosen}
          disabled={busy || !onChoose}
          onChange={(e) => choose(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="blank">Nothing — filled in inside the app</MenuItem>
          {/* Rendered from what the kernel said could satisfy this, so a
              connector added to the kernel appears here on its own. */}
          {options.map((o) => (
            <MenuItem key={o.source} value={`connector:${o.source}`}>
              {o.source}
            </MenuItem>
          ))}
        </TextField>
        {busy ? <Typography variant="body2">Saving…</Typography> : null}
      </Stack>

      {options.length ? null : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nothing this kernel knows about produces these records, so the honest
          options are to leave it blank or to wait for a connector that does.
          An empty list here is an answer rather than a gap.
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {describe(chosen)}
      </Typography>

      {requirement.choice?.chosen_by ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Chosen by {requirement.choice.chosen_by} on{" "}
          {String(requirement.choice.chosen_at).slice(0, 10)}. Stamped by the
          kernel, not asserted by this console.
        </Typography>
      ) : null}

      {failure ? (
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {failure}
        </Alert>
      ) : null}
    </Box>
  );
}

function InstallCard({ install, onChoose }) {
  if (install.unparseable) {
    return (
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h2">{install.app_type}</Typography>
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          <AlertTitle>This manifest does not parse</AlertTitle>
          {install.detail} Shown rather than skipped, for the same reason it is
          shown on the Apps screen: this is the one place you would never find
          out otherwise.
        </Alert>
      </Paper>
    );
  }

  const state = STATES[install.state] || STATES.configured;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "baseline", flexWrap: "wrap" }}
      >
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {install.app_type}
        </Typography>
        {/* Colour is never the only signal: the chip carries the words too. */}
        <Chip
          size="small"
          variant="outlined"
          label={state.label}
          sx={{ color: state.colour, borderColor: state.colour }}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {state.line}
      </Typography>

      {install.requires?.length ? (
        <Stack spacing={2.5} sx={{ mt: 2 }} divider={<Divider flexItem />}>
          {install.requires.map((r) => (
            <Requirement
              key={r.key}
              appType={install.app_type}
              requirement={r}
              onChoose={onChoose}
            />
          ))}
        </Stack>
      ) : null}
    </Paper>
  );
}

export default function Installs({ get, post, onEffective }) {
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get("/installs"));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    load();
    // Declarations and the answers given to them — no records, so there is no
    // tag intersection to report. Saying so beats leaving the strip showing
    // whatever the last section resolved.
    onEffective(null);
  }, [load, onEffective]);

  if (loading && !data) return <Loading label="Reading what each app needs" />;
  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }

  const installs = data?.installs || [];
  const outstanding = installs.filter(
    (i) => i.state === "not_configured" || i.state === "incomplete",
  );

  // Two groups, and the split is the one the screen is about: an app that
  // declared what it needs, and an app that never did. A manifest that does
  // not parse is asked about either way — it is the one screen where you
  // would never find that out otherwise.
  const asking = installs.filter(
    (i) => i.unparseable || i.requires?.length,
  );
  const quiet = installs.filter(
    (i) => !i.unparseable && !i.requires?.length,
  );

  const choose = post
    ? async (appType, requirement, source) => {
        await post(`/installs/${appType}/${requirement}`, { source });
        // Re-read rather than patching locally: the kernel may have refused
        // part of what was sent, and a screen showing what it asked for
        // rather than what was stored is the failure this product is against.
        await load();
      }
    : null;

  return (
    <Stack spacing={2}>
      {/* The unanswered questions are the screen. Zero of them is a good
          answer and reads as one — a large `0` above "apps waiting on an
          answer" is the shape of "nothing needs you", which is exactly what
          somebody opening this wants to find out. */}
      <Headline
        eyebrow="Waiting on an answer"
        figure={outstanding.length.toLocaleString()}
        tone={outstanding.length ? "attention" : "permitted"}
        caption={
          outstanding.length === 1
            ? "app cannot say where its data comes from"
            : "apps cannot say where their data comes from"
        }
        basis="Until each of these has an answer, an app with no data and an app working exactly as intended look the same on every screen. Answering is what makes the difference sayable. An app that declares no requirements is not counted here — it never asked."
      />

      {outstanding.length ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>
            {outstanding.length} app{outstanding.length === 1 ? "" : "s"} with
            an unanswered question
          </AlertTitle>
          Until each of these has an answer, an app with no data and an app
          working exactly as intended look the same on every screen. Answering
          is what makes the difference sayable.
        </Alert>
      ) : null}

      {/* The apps that have a question, in full. This screen is named after
          them and they were eleventh on the page.

          Everything with nothing to answer used to render a card carrying the
          same sixty-word paragraph — eleven identical copies of it, a
          thousand words of which about a hundred were not repeated. The
          sentence is true and it is said once now, under the count, with the
          apps it is about as chips. That is not a summary of the paragraph;
          it is the paragraph, printed the number of times it says something
          new. */}
      {asking.length ? (
        <Stack spacing={2}>
          {asking.map((i) => (
            <InstallCard key={i.app_type} install={i} onChoose={choose} />
          ))}
        </Stack>
      ) : null}

      {quiet.length ? (
        <Box sx={{ pt: asking.length ? 1 : 0 }}>
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            {quiet.length} app{quiet.length === 1 ? "" : "s"} with nothing to
            answer
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mb: 1 }}>
            {quiet.map((i) => (
              <Chip
                key={i.app_type}
                size="small"
                variant="outlined"
                label={i.app_type}
                sx={{ fontFamily: "monospace" }}
              />
            ))}
          </Stack>
          <Disclose
            summary="Each declares no requirements, so there is nothing to point at a source."
            label="What that means"
          >
            <Typography variant="body2" color="text.secondary">
              That is a fact about the manifest rather than about the app — a{" "}
              <code>requires</code> block is how an app says what it needs, and
              these have not got one. An app with no requirements is not an app
              that is finished being set up; it is an app that has never said
              what being set up would mean.
            </Typography>
          </Disclose>
        </Box>
      ) : null}

      {installs.length ? null : (
        <EmptyState title="No apps in this kernel">
          <Typography variant="body2">
            Nothing holds a manifest here yet, so there is nothing to install.
          </Typography>
        </EmptyState>
      )}


    </Stack>
  );
}
